import type { TFunction } from 'i18next'
import type { FormQuestion } from '@/lib/services/form.service'

/**
 * Regras da resposta de uma pergunta de texto, do lado de quem preenche.
 *
 * Location: lib
 * Why: o lojista pode pedir só números e um mínimo e um máximo de caracteres.
 * Quem recusa de verdade é a API, no envio; aqui é para a pessoa ver a regra
 * junto da pergunta e saber o que arrumar antes de enviar. A conta é a mesma
 * de lá: espaço nas pontas não entra, e um emoji ou letra acentuada vale um.
 */
export type AnswerViolation = 'numbers_only' | 'too_short' | 'too_long'

const TEXTO = new Set(['SHORT_TEXT', 'LONG_TEXT'])

export const isTextQuestion = (question: FormQuestion): boolean => TEXTO.has(question.type)

/** Conta por caractere, não por unidade UTF-16: um par substituto (emoji) vale um. */
export function answerLength(texto: string): number {
  const limpo = texto.trim()
  let total = 0
  for (let i = 0; i < limpo.length; i++) {
    const codigo = limpo.charCodeAt(i)
    if (codigo >= 0xd800 && codigo <= 0xdbff && i + 1 < limpo.length) i++
    total++
  }
  return total
}

export function answerViolation(question: FormQuestion, answer: string): AnswerViolation | null {
  if (!isTextQuestion(question)) return null
  const texto = answer.trim()
  if (!texto) return null
  if (question.numbersOnly && !/^[0-9]+$/.test(texto)) return 'numbers_only'
  const tamanho = answerLength(texto)
  if (question.minLength != null && tamanho < question.minLength) return 'too_short'
  if (question.maxLength != null && tamanho > question.maxLength) return 'too_long'
  return null
}

/** As regras em frases, para mostrar junto da pergunta. Sem regra, lista vazia. */
export function answerRuleTexts(question: FormQuestion, t: TFunction): string[] {
  if (!isTextQuestion(question)) return []
  const { minLength: min, maxLength: max } = question
  const frases: string[] = []
  if (question.numbersOnly) frases.push(t('store.forms.rule_numbers'))
  if (min != null && max != null) {
    frases.push(min === max ? t('store.forms.rule_exact', { count: max }) : t('store.forms.rule_range', { min, count: max }))
  } else if (min != null) frases.push(t('store.forms.rule_min', { count: min }))
  else if (max != null) frases.push(t('store.forms.rule_max', { count: max }))
  return frases
}

/** A mensagem de erro de uma resposta fora da regra. */
export function answerViolationText(question: FormQuestion, violation: AnswerViolation, t: TFunction): string {
  if (violation === 'numbers_only') return t('store.forms.error_numbers', { question: question.question })
  if (violation === 'too_short') return t('store.forms.error_min', { question: question.question, count: question.minLength ?? 0 })
  return t('store.forms.error_max', { question: question.question, count: question.maxLength ?? 0 })
}
